import { getErrorCopy } from '@/shared/copy/errors';
import { getMessage } from '@/shared/copy/messages';
import { ApiError } from '@/shared/data/client';
import { emitAuthDiagnostic } from './authDiagnostics';
import {
  traceAuthRefreshCompleted,
  traceAuthRefreshFailed,
  traceAuthRefreshStarted,
} from './refreshTraceEvents';
import { setClerkProvisioningFlag, setManualSignOutFlag } from './sessionStorage';
import {
  isAccountNotFoundError,
  isIdentityMismatchError,
  isSessionTokenExpiredError,
  mergeSessionFallbacks,
  shouldPreserveSessionOnRefreshMiss,
  shouldPreserveSessionOnSignedOutSignal,
  type RefreshOutcome,
} from './useAuth.helpers';
import type { AuthError, AuthSession } from './types';

type ExecuteAuthRefreshArgs = {
  applySession: (next: AuthSession | null) => void;
  clearAuthRecoveryFailures: () => void;
  currentSession: AuthSession | null;
  expireSession: (reason: 'timer' | 'refresh_error' | 'session_expired_event') => void;
  forceRefresh: boolean;
  loadCurrentClerkSession: (input?: { forceTokenRefresh?: boolean }) => Promise<AuthSession | null>;
  resolveHasSignedInSignal: () => boolean;
  syncFromStorage: boolean;
  setError: (error: AuthError | null) => void;
};

export const executeAuthRefresh = async ({
  applySession,
  clearAuthRecoveryFailures,
  currentSession,
  expireSession,
  forceRefresh,
  loadCurrentClerkSession,
  resolveHasSignedInSignal,
  syncFromStorage,
  setError,
}: ExecuteAuthRefreshArgs): Promise<RefreshOutcome> => {
  try {
    const hasSignedInSignal = resolveHasSignedInSignal();
    traceAuthRefreshStarted({
      userId: currentSession?.userId ?? null,
      forceRefresh,
      hasSignedInSignal,
      hasCurrentSession: currentSession !== null,
    });
    if (!hasSignedInSignal && !syncFromStorage) {
      if (
        shouldPreserveSessionOnSignedOutSignal({
          hasSignedInSignal,
          currentSession,
        })
      ) {
        traceAuthRefreshCompleted({
          userId: currentSession?.userId ?? null,
          forceRefresh,
          outcome: 'preserved_signed_out_signal',
          didRefresh: false,
          expiresAt: currentSession?.expiresAt ?? null,
        });
        return { session: currentSession, didRefresh: false };
      }
      applySession(null);
      traceAuthRefreshCompleted({
        userId: currentSession?.userId ?? null,
        forceRefresh,
        outcome: 'cleared_signed_out_signal',
        didRefresh: false,
        expiresAt: null,
      });
      return { session: null, didRefresh: false };
    }

    const refreshed = await loadCurrentClerkSession({ forceTokenRefresh: forceRefresh });
    if (!refreshed) {
      if (
        !syncFromStorage &&
        shouldPreserveSessionOnRefreshMiss({ refreshedSession: refreshed, currentSession })
      ) {
        traceAuthRefreshCompleted({
          userId: currentSession?.userId ?? null,
          forceRefresh,
          outcome: 'preserved_refresh_miss',
          didRefresh: false,
          expiresAt: currentSession?.expiresAt ?? null,
        });
        return { session: currentSession, didRefresh: false };
      }
      applySession(null);
      traceAuthRefreshCompleted({
        userId: currentSession?.userId ?? null,
        forceRefresh,
        outcome: 'cleared_refresh_miss',
        didRefresh: false,
        expiresAt: null,
      });
      return { session: null, didRefresh: false };
    }

    setError(null);
    clearAuthRecoveryFailures();
    const merged = mergeSessionFallbacks(refreshed, currentSession);
    applySession(merged);
    traceAuthRefreshCompleted({
      userId: merged.userId,
      forceRefresh,
      outcome: 'refreshed',
      didRefresh: true,
      expiresAt: merged.expiresAt ?? null,
    });
    return { session: merged, didRefresh: true };
  } catch (err) {
    const apiError = err instanceof ApiError ? err : null;
    traceAuthRefreshFailed({
      userId: currentSession?.userId ?? null,
      forceRefresh,
      status: apiError?.status ?? null,
      code: apiError?.code ?? null,
      error: err,
    });

    if (isIdentityMismatchError(err)) {
      emitAuthDiagnostic('auth_refresh_forced_expire', {
        branch: 'authoritative_auth_error',
        userId: currentSession?.userId ?? null,
        forceRefresh,
        status: apiError?.status ?? null,
        code: apiError?.code ?? null,
        message: apiError?.message ?? (err instanceof Error ? err.message : String(err)),
      });
      setError({ code: 'AUTH_ERROR', message: getErrorCopy('auth.refreshSessionFailed') });
      traceAuthRefreshCompleted({
        userId: currentSession?.userId ?? null,
        forceRefresh,
        outcome: 'expired_auth_error',
        didRefresh: false,
        expiresAt: currentSession?.expiresAt ?? null,
      });
      expireSession('refresh_error');
      return { session: null, didRefresh: false };
    }

    if (isAccountNotFoundError(err)) {
      emitAuthDiagnostic('auth_refresh_missing_user_terminal', {
        userId: currentSession?.userId ?? null,
        forceRefresh,
        status: apiError?.status ?? null,
        code: apiError?.code ?? null,
        message: apiError?.message ?? (err instanceof Error ? err.message : String(err)),
      });
      setError({
        code: 'AUTH_ERROR',
        message: getMessage('auth.signIn.accountNotFound'),
      });
      setClerkProvisioningFlag(false);
      setManualSignOutFlag(true);
      applySession(null);
      traceAuthRefreshCompleted({
        userId: currentSession?.userId ?? null,
        forceRefresh,
        outcome: 'missing_user_terminal',
        didRefresh: false,
        expiresAt: null,
      });
      return { session: null, didRefresh: false };
    }

    if (err instanceof ApiError && (err.status === 429 || err.code === 'RATE_LIMITED')) {
      traceAuthRefreshCompleted({
        userId: currentSession?.userId ?? null,
        forceRefresh,
        outcome: 'rate_limited',
        didRefresh: false,
        expiresAt: currentSession?.expiresAt ?? null,
      });
      return { session: currentSession, didRefresh: false };
    }

    if (currentSession && err instanceof ApiError && isSessionTokenExpiredError(err)) {
      emitAuthDiagnostic('auth_refresh_forced_expire', {
        branch: 'expired_session_token',
        userId: currentSession.userId,
        forceRefresh,
        status: err.status ?? null,
        code: err.code ?? null,
        message: err.message,
        correlationId: err.correlationId ?? null,
        errorId: err.errorId ?? null,
      });
      setError({ code: 'AUTH_ERROR', message: getErrorCopy('auth.refreshSessionFailed') });
      traceAuthRefreshCompleted({
        userId: currentSession.userId,
        forceRefresh,
        outcome: 'expired_auth_error',
        didRefresh: false,
        expiresAt: currentSession.expiresAt ?? null,
      });
      expireSession('refresh_error');
      return { session: null, didRefresh: false };
    }

    if (currentSession && err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      setError({ code: 'AUTH_ERROR', message: getErrorCopy('auth.refreshSessionFailed') });
      traceAuthRefreshCompleted({
        userId: currentSession.userId,
        forceRefresh,
        outcome: 'expired_auth_error',
        didRefresh: false,
        expiresAt: currentSession.expiresAt ?? null,
      });
      if (forceRefresh) {
        emitAuthDiagnostic('auth_refresh_forced_expire', {
          branch: 'authoritative_auth_error',
          userId: currentSession.userId,
          forceRefresh,
          status: err.status ?? null,
          code: err.code ?? null,
          message: err.message,
          correlationId: err.correlationId ?? null,
          errorId: err.errorId ?? null,
        });
        expireSession('refresh_error');
        return { session: null, didRefresh: false };
      }
      return { session: currentSession, didRefresh: false };
    }

    setError({ code: 'AUTH_ERROR', message: getErrorCopy('auth.refreshSessionFailed') });
    traceAuthRefreshCompleted({
      userId: currentSession?.userId ?? null,
      forceRefresh,
      outcome: 'expired_unexpected_error',
      didRefresh: false,
      expiresAt: currentSession?.expiresAt ?? null,
    });
    emitAuthDiagnostic('auth_refresh_forced_expire', {
      branch: 'unexpected_error',
      userId: currentSession?.userId ?? null,
      forceRefresh,
      status: apiError?.status ?? null,
      code: apiError?.code ?? null,
      message: apiError?.message ?? (err instanceof Error ? err.message : String(err)),
    });
    expireSession('refresh_error');
    return { session: null, didRefresh: false };
  }
};
